import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/ja';
import { useTranslation } from 'react-i18next';
import CustomSwitch from '../../components/Switch/Switch';
import { LANG_TEXT } from '../../constants/language';
import { LANG_DISPLAY_KEY } from '../../consts/language';
import { useGameScheduleContext } from '../../context/GameScheduleContext';
import usePermission from '../../hooks/usePermission';
import TimeBox from './components/CalendarControls/TimeBox';
import { IGameSchedule } from './gameSchedule.types';
import { foodTruckGameStyles } from './styles';

const DAY_COLOR = {
  [LANG_TEXT.WEEK_DAY]: '#475467',
  [LANG_TEXT.WEEK_END]: '#D92D20',
  [LANG_TEXT.HOLIDAY]: '#30893C',
};

export default function FoodTruckGameSchedule() {
  const classes = foodTruckGameStyles();

  const { gameSchedules, updateGameSchedule, loading } = useGameScheduleContext();
  const { restaurantId } = usePermission();

  const { t } = useTranslation();

  const lang = localStorage.getItem(LANG_DISPLAY_KEY) || 'ja';
  dayjs.locale(lang);

  const groupedSchedules = gameSchedules.reduce(
    (acc, schedule) => {
      const date = dayjs(schedule.date);
      const monthYear = lang === 'ja' ? date.format('YYYY年MM月') : date.format('MMMM YYYY');

      if (!acc[monthYear]) {
        acc[monthYear] = [];
      }
      acc[monthYear].push(schedule);

      return acc;
    },
    {} as Record<string, IGameSchedule[]>
  );

  const formatTime = (hours: number, minutes: number) => {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    const date = dayjs(dateString);

    if (lang === 'ja') {
      return date.format('YYYY/MM/DD');
    } else {
      return date.format('MMM D, YYYY');
    }
  };

  const getDayType = (schedule: IGameSchedule) => {
    const date = dayjs(schedule.date);
    const dayOfWeek = date.day();
    const weekdayName = lang === 'ja' ? date.localeData().weekdays(date).slice(0, 1)[0] : date.format('dd');

    if (schedule.is_holiday) {
      return { label: weekdayName, color: DAY_COLOR[LANG_TEXT.HOLIDAY] };
    }
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return { label: weekdayName, color: DAY_COLOR[LANG_TEXT.WEEK_END] };
    }
    return { label: weekdayName, color: DAY_COLOR[LANG_TEXT.WEEK_DAY] };
  };

  const handleScheduleChange = async (scheduleId: string, isChecked: boolean) => {
    const schedule = gameSchedules.find((s) => s.id === scheduleId);
    if (!schedule || !restaurantId) return;

    const currentTrucks = schedule.available_food_trucks || [];
    let updatedTrucks: string[];

    if (isChecked) {
      if (!currentTrucks.includes(restaurantId)) {
        updatedTrucks = [...currentTrucks, restaurantId];
      } else {
        updatedTrucks = currentTrucks;
      }
    } else {
      updatedTrucks = currentTrucks.filter((id) => id !== restaurantId);
    }

    try {
      await updateGameSchedule(scheduleId, { available_food_trucks: updatedTrucks });
    } catch (error) {
      console.error('Failed to update game schedule:', error);
    }
  };

  if (loading) {
    return (
      <Box component="main" className={classes.Container}>
        ....Loading
      </Box>
    );
  }

  if (gameSchedules.length === 0) {
    return (
      <Box component="main" className={classes.Container}>
        No data
      </Box>
    );
  }

  return (
    <Box component="main" className={classes.Container}>
      <Box component="main" className={classes.ScheduleList}>
        {Object.entries(groupedSchedules).map(([monthYear, schedules]) => (
          <Box key={monthYear} className={classes.MonthContainer}>
            <Typography fontSize={16} lineHeight={'24px'} fontWeight={600} color={'#344054'} width={200}>
              {t(LANG_TEXT.GAME_IN, { monthYear })}
            </Typography>
            <Box className={classes.ScheduleBox}>
              {schedules.map((schedule) => {
                const dayInfo = getDayType(schedule);
                const isAttending = schedule.available_food_trucks?.includes(restaurantId || '') || false;
                return (
                  <Box key={schedule.id} className={classes.ScheduleItem}>
                    <TimeBox label="" value={formatDate(schedule.date)} width={140} />
                    <TimeBox label="" value={dayInfo.label} width="fit-content" color={dayInfo.color} />
                    <TimeBox label={t(LANG_TEXT.GAME_STARTS)} value={formatTime(schedule.starttime_hh || 0, schedule.starttime_mm || 0)} width={140} />
                    <TimeBox label={t(LANG_TEXT.DELIVERY_STARTS)} value={formatTime(schedule.delivery_hh || 0, schedule.delivery_mm || 0)} width={140} />
                    <CustomSwitch
                      isChecked={isAttending}
                      onChange={(isChecked) => handleScheduleChange(schedule.id, isChecked)}
                      className={classes.AttendSwitch}
                      label={t(LANG_TEXT.ATTEND)}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
      <Box className={classes.ReferenceBox}>
        {Object.entries(DAY_COLOR).map(([key, color]) => (
          <Box key={key} sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <FiberManualRecordIcon sx={{ color: color, fontSize: 14 }} />
            <Typography fontSize={14} lineHeight={'20px'} color={color} fontWeight={600}>
              {t(key as LANG_TEXT)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
